import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Link, Center } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AleaDownloadIcon from '../../icons/AleaDownloadIcon';

// Interface for menu option
export interface DownloadOption {
  id: string;            // Unique identifier
  label: string;         // Display text
  translationKey?: string; // Translation key (uses label if not provided)
  onClick: () => void;   // Click handler
  disabled?: boolean;    // Disabled state
}

interface AleaDownloadButtonProps {
  // Array for dynamic options
  options: DownloadOption[];
  // Custom style properties
  iconSize?: string;
  buttonSize?: string;
  buttonBg?: string;
  buttonHoverBg?: string;
  dropdownWidth?: string;
  /** Align dropdown to open leftward (default) or rightward from the button */
  dropdownAlign?: 'left' | 'right';
}

const AleaDownloadButton: React.FC<AleaDownloadButtonProps> = ({
  options = [],
  iconSize = "22",
  buttonSize = "50px",
  buttonBg = "white",
  buttonHoverBg = "#f8f8f8",
  dropdownWidth = "196px",
  dropdownAlign = 'left',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = (option: DownloadOption) => {
    if (!option.disabled && option.onClick) {
      option.onClick();
    }
    setIsOpen(false);
  };

  // z-index values
  const backdropZIndex = 1500;
  const buttonZIndex = 1600;
  const dropdownZIndex = 1600;

  // Don't show the button if there are no options
  if (options.length === 0) {
    return null;
  }

  return (
    <Box ref={menuRef} position="relative">
      {/* Backdrop */}
      {isOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.25)"
          zIndex={backdropZIndex}
          onClick={() => setIsOpen(false)}
        />
      )}

      <Button
        onClick={handleToggleMenu}
        height={buttonSize}
        width={buttonSize}
        borderRadius="20px"
        bg={buttonBg}
        border="1px solid #DCDCDC"
        _hover={{ bg: buttonHoverBg }}
        p={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        zIndex={isOpen ? buttonZIndex : 'auto'}
      >
        <Center w="100%" h="100%">
          <AleaDownloadIcon size={iconSize} />
        </Center>
      </Button>

      {/* Download Dropdown */}
      {isOpen && options.length > 0 && (
        <Box
          position="absolute"
          top="calc(100% + 5px)"
          {...(dropdownAlign === 'right' ? { left: 0 } : { right: 0 })}
          bg="white"
          borderRadius="20px"
          overflow="hidden"
          w={dropdownWidth}
          border="1px solid"
          borderColor="#DCDCDC"
          zIndex={dropdownZIndex}
        >
          {options.map((option, index) => (
            <Box 
              key={option.id} 
              borderBottom={index < options.length - 1 ? "1px solid" : "none"}
              borderColor="#DCDCDC"
            >
              <Link
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                h="50px"
                onClick={() => handleMenuItemClick(option)}
                variant="outline"
                color={option.disabled ? "gray.400" : "black"}
                cursor={option.disabled ? "not-allowed" : "pointer"}
                opacity={option.disabled ? 0.6 : 1}
                pl="20px"
                _hover={{ 
                  bg: option.disabled ? "transparent" : "#f8f8f8", 
                  textDecoration: "none" 
                }}
              >
                {option.translationKey ? t(option.translationKey) : option.label}
              </Link>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AleaDownloadButton; 